import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import {
  allTemplatesQueryOptions,
  TEMPLATE_VARIABLES,
  type SiteTemplate,
} from "@/lib/templates.queries";
import { createTemplate, deleteTemplate, updateTemplate } from "@/lib/templates.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, ExternalLink, Copy, Braces, FileText } from "lucide-react";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";
import { pageLayoutsQueryOptions } from "@/lib/layouts.queries";
import { MediaField } from "@/components/admin/MediaField";
import { MEDIA_TARGETS } from "@/lib/media-buckets";

export const Route = createFileRoute("/_authenticated/admin/templates")({
  component: TemplatesAdminPage,
});

type FormState = {
  id?: string;
  slug: string;
  name: string;
  description: string;
  hero_image_path: string;
  hero_kicker: string;
  hero_title: string;
  hero_subtitle: string;
  body_md: string;
  seo_title: string;
  seo_description: string;
  is_published: boolean;
};

const empty: FormState = {
  slug: "",
  name: "",
  description: "",
  hero_image_path: "",
  hero_kicker: "",
  hero_title: "",
  hero_subtitle: "",
  body_md: "",
  seo_title: "",
  seo_description: "",
  is_published: true,
};

function TemplatesAdminPage() {
  // The admin list, not the public one: unpublished pages must stay listed
  // here, or switching Published off could never be undone.
  const { data: templates = [] } = useQuery(allTemplatesQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createTemplate);
  const update = useServerFn(updateTemplate);
  const del = useServerFn(deleteTemplate);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [q, setQ] = useState("");
  const dirty = useDirty(form, open);
  // True while the MediaField's library picker (sibling modal) is up.
  const [pickerBusy, setPickerBusy] = useState(false);

  // Populated by RichTextEditor; appends HTML at the end of the body.
  const insertHtmlRef = useRef<((html: string) => void) | null>(null);
  const { data: layouts = [] } = useQuery(pageLayoutsQueryOptions());
  const activeLayouts = useMemo(() => layouts.filter((l) => l.is_active), [layouts]);
  // Remounting the picker returns it to the placeholder — it is an action, not a value.
  const [layoutPickerKey, setLayoutPickerKey] = useState(0);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    const sorted = [...templates].sort((a, b) => a.name.localeCompare(b.name));
    if (!n) return sorted;
    return sorted.filter(
      (t) => t.name.toLowerCase().includes(n) || t.slug.toLowerCase().includes(n),
    );
  }, [templates, q]);

  function loadEdit(t: SiteTemplate) {
    setForm({
      id: t.id,
      slug: t.slug,
      name: t.name,
      description: t.description ?? "",
      // Carried through every save: the form used to leave this out, and the
      // update then stored it as empty, wiping the page's header image.
      hero_image_path: t.hero_image_path ?? "",
      hero_kicker: t.hero_kicker ?? "",
      hero_title: t.hero_title ?? "",
      hero_subtitle: t.hero_subtitle ?? "",
      body_md: t.body_md ?? "",
      seo_title: t.seo_title ?? "",
      seo_description: t.seo_description ?? "",
      is_published: t.is_published,
    });
    setOpen(true);
  }

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        slug: form.slug,
        name: form.name,
        description: form.description || null,
        hero_image_path: form.hero_image_path || null,
        hero_kicker: form.hero_kicker || null,
        hero_title: form.hero_title || null,
        hero_subtitle: form.hero_subtitle || null,
        body_md: form.body_md ?? "",
        seo_title: form.seo_title || null,
        seo_description: form.seo_description || null,
        is_published: form.is_published,
      };
      if (form.id) await update({ data: { id: form.id, ...payload } });
      else await create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_templates"] });
      toast.success("Saved");
      setOpen(false);
      setForm(empty);
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_templates"] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e.message ?? "Delete failed"),
  });

  function insertToken(tok: string) {
    setForm((f) => ({ ...f, body_md: `${f.body_md || ""}\n<p>${tok}</p>\n` }));
  }

  function insertLayout(id: string) {
    const layout = activeLayouts.find((l) => l.id === id);
    if (!layout) return;
    const insert = insertHtmlRef.current;
    if (!insert) {
      toast.error("Editor is not ready yet");
      return;
    }
    insert(layout.body_html);
    toast.success(`Inserted "${layout.name}"`);
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Site Templates</CardTitle>
            <CardDescription>
              CMS pages rendered at <code>/p/&lt;slug&gt;</code>. Drop template variables into the
              body to insert live data sections (team list, sponsors, advisory board, hero
              slideshow, etc.). Unpublished pages stay listed here and can be switched back on
              in the editor.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-56"
            />
            <Button
              onClick={() => {
                setForm(empty);
                setOpen(true);
              }}
            >
              <Plus className="mr-1 h-4 w-4" /> New template
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-card shadow-sm">
                <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="p-3">Name</th>
                  <th className="p-3">Slug</th>
                  <th className="p-3">Published</th>
                  <th className="p-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.id} className="border-b hover:bg-secondary/30">
                    <td
                      className={`p-3 font-medium ${t.is_published ? "" : "text-muted-foreground"}`}
                    >
                      {t.name}
                    </td>
                    <td className="p-3 text-muted-foreground">{t.slug}</td>
                    <td className="p-3">
                      <Badge variant={t.is_published ? "default" : "secondary"}>
                        {t.is_published ? "Published" : "Unpublished"}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      <a
                        href={`/p/${t.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="mr-1 inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-secondary"
                        title="Preview"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                      <Button variant="ghost" size="icon" onClick={() => loadEdit(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (confirm(`Delete template "${t.name}"?`)) remove.mutate(t.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="p-6 text-center text-muted-foreground">
                      No templates.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        size="lg"
        icon={FileText}
        title={form.id ? "Edit template" : "New template"}
        context={form.id ? `${form.name} — /p/${form.slug}` : "CMS page rendered at /p/<slug>"}
        dirty={dirty}
        busy={pickerBusy || save.isPending}
        asForm
        onSubmit={() => {
          if (form.name && form.slug) save.mutate();
        }}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <SaveButton dirty={dirty} pending={save.isPending} disabled={!form.name || !form.slug} />
          </>
        }
      >
        <div className="space-y-4">
          <FormSection title="Page">
            <Field label="Name" required>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Slug (URL)" required>
              <Input
                required
                value={form.slug}
                onChange={(e) =>
                  setForm({
                    ...form,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                  })
                }
              />
            </Field>
            <Field label="Internal description" full>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </Field>
          </FormSection>

          <FormSection title="Hero">
            <Field label="Kicker">
              <Input
                value={form.hero_kicker}
                onChange={(e) => setForm({ ...form, hero_kicker: e.target.value })}
              />
            </Field>
            <Field label="Title">
              <Input
                value={form.hero_title}
                onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
              />
            </Field>
            <Field label="Subtitle / lede" full>
              <Textarea
                rows={2}
                value={form.hero_subtitle}
                onChange={(e) => setForm({ ...form, hero_subtitle: e.target.value })}
              />
            </Field>
            <div className="sm:col-span-2">
              <MediaField
                label="Header image"
                value={form.hero_image_path}
                target={MEDIA_TARGETS.templateHero}
                onChange={(url) => setForm((f) => ({ ...f, hero_image_path: url }))}
                onPickerOpenChange={setPickerBusy}
                hint="Shown behind the page title on pages that have header art. Leave blank to use the default."
              />
            </div>
          </FormSection>

          <FormSection title="Body" cols={1}>
            <div className="-mt-1 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-muted-foreground">
                Use the toolbar to format, link, and insert images. Template variables (e.g.{" "}
                <code>[LIST:OurTeam]</code>) render live data when placed on their own paragraph.
                Layouts are appended to the end of the body.
              </p>
              <div className="flex flex-wrap items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs"
                >
                  <Braces className="h-3.5 w-3.5" />
                  Template variables
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Template variables
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Click Insert to drop one at the cursor. Each must sit on its own line to
                  render full-width.
                </p>
                <div className="mt-3 max-h-[45vh] space-y-2 overflow-y-auto pr-1">
                  {TEMPLATE_VARIABLES.map((v) => (
                    <div key={v.token} className="rounded-md border bg-secondary/30 p-2">
                      <div className="flex items-center justify-between gap-2">
                        <code className="text-xs font-semibold">{v.token}</code>
                        <div className="flex gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              navigator.clipboard
                                .writeText(v.token)
                                .then(() => toast.success("Copied"))
                            }
                            title="Copy"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => insertToken(v.token)}
                          >
                            Insert
                          </Button>
                        </div>
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground">
                        <span className="font-medium text-foreground">{v.label}.</span>{" "}
                        {v.description}
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
                {form.slug && (
                  <a
                    href={`/p/${form.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Preview /p/{form.slug} <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {activeLayouts.length > 0 && (
                  <Select
                    key={layoutPickerKey}
                    onValueChange={(v) => {
                      insertLayout(v);
                      setLayoutPickerKey((k) => k + 1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-44 text-xs">
                      <SelectValue placeholder="Insert layout…" />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLayouts.map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
            <RichTextEditor
              value={form.body_md}
              onChange={(html) => setForm((f) => ({ ...f, body_md: html }))}
              uploadFolder="templates"
              minHeight={360}
              placeholder="Write the page body…"
              insertHtmlRef={insertHtmlRef}
              allowSource
            />
          </FormSection>

          <FormSection title="SEO and publishing">
            <Field label="SEO title">
              <Input
                value={form.seo_title}
                onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
              />
            </Field>
            <Field label="SEO description">
              <Input
                value={form.seo_description}
                onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
              />
            </Field>
            <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2 sm:col-span-2">
              <Switch
                id="tpl-published"
                checked={form.is_published}
                onCheckedChange={(v) => setForm({ ...form, is_published: v })}
              />
              <Label htmlFor="tpl-published" className="cursor-pointer">Published</Label>
            </div>
          </FormSection>
        </div>
      </FormDialog>
    </div>
  );
}
